import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { UserContext } from "../UserContext";
import { BaseURL } from "../BaseURL";
import {
  HiSparkles,
  HiXMark,
  HiOutlineDocumentText,
  HiOutlineChatBubbleLeftRight,
} from "react-icons/hi2";
import { IoSend } from "react-icons/io5";

// The chatbot answers in lightweight markdown (**bold**, "- " bullet lists,
// blank-line-separated paragraphs). Rendered as plain text that shows up as
// literal asterisks and dashes, so turn it into real HTML before it hits the
// DOM -- sanitized since it's LLM output, not developer-authored markup.
const escapeHtml = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const formatAssistantText = (text) => {
  const withBold = escapeHtml(text).replace(
    /\*\*(.+?)\*\*/g,
    "<strong>$1</strong>"
  );

  let html = "";
  let inList = false;
  for (const line of withBold.split("\n")) {
    const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (heading) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      html += `<h4>${heading[1]}</h4>`;
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      html += `<li>${bullet[1]}</li>`;
      continue;
    }
    if (inList) {
      html += "</ul>";
      inList = false;
    }
    html += line.trim() ? `<p>${line}</p>` : "<br />";
  }
  if (inList) html += "</ul>";

  return DOMPurify.sanitize(html);
};

// Grounded Q&A over each product's own published docs, answered by Claude.
// Works for both CodeNavigator and Continuum -- the product currently active
// in UserContext decides which backend route (and doc corpus) gets queried.
export default function AskDocsChat() {
  const { darkTheme, product, userLoginInfo } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Gated by the "View Chatbot" role permission (view-chatbot), returned as
  // can_view_chatbot on the personal/MS login response and cached in the
  // uInfo localStorage blob -- see PersonalController::login/MsController::login.
  const canUseChat = !!userLoginInfo?.can_view_chatbot;

  const isContinuum = product === "continuum";
  const productLabel = isContinuum ? "Continuum" : "CodeNavigator";
  const routePrefix = isContinuum ? "continuum" : "codenavigator";

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  const authToken = useMemo(
    () =>
      localStorage.getItem("cn_access_token") ||
      localStorage.getItem("access_token") ||
      "",
    []
  );

  const handleOpen = () => {
    setOpen(true);
    if (messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          text: `Ask me anything about ${productLabel} -- I'll answer straight from the docs.`,
        },
      ]);
    }
  };

  const handleClose = () => {
    setEntered(false);
    setTimeout(() => setOpen(false), 180);
  };

  const handleSend = async () => {
    const question = input.trim();
    if (!question || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: question }]);
    setLoading(true);

    try {
      const versionNameId = localStorage.getItem("versionNameId") || "";
      const res = await fetch(`${BaseURL}/${routePrefix}/chatbot/ask`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ query: question, version: versionNameId }),
      });
      const data = await res.json();

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: res.ok
            ? data?.answer || "I couldn't generate a response for that."
            : data?.message || "Something went wrong. Please try again.",
          sources: data?.sources || [],
          version: data?.version || null,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "I couldn't reach the docs assistant. Please check your connection and try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!canUseChat) return null;

  const isEmptyState = messages.length <= 1;

  return (
    <>
      <button
        onClick={handleOpen}
        aria-label="Ask the docs"
        className={`fixed bottom-8 right-5 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-[linear-gradient(135deg,#0A4DDC_0%,#5b3df0_100%)] text-White shadow-[0_10px_30px_-8px_rgba(10,77,220,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_-8px_rgba(10,77,220,0.65)] ${
          open ? "pointer-events-none scale-90 opacity-0" : "scale-100 opacity-100"
        }`}
      >
        <span className="absolute inset-0 -z-10 rounded-full bg-[#5b3df0] opacity-40 blur-md animate-pulse" />
        <HiOutlineChatBubbleLeftRight size={26} />
        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-White bg-[#5b3df0] text-White">
          <HiSparkles size={10} />
        </span>
      </button>

      {open && (
        <div
          className={`fixed bottom-20 right-5 z-[9999] flex w-[min(410px,92vw)] h-[min(76vh,680px)] flex-col overflow-hidden rounded-[28px] border shadow-[0_24px_70px_-20px_rgba(10,77,220,0.4)] transition-all duration-200 ease-out ${
            entered
              ? "translate-y-0 scale-100 opacity-100"
              : "translate-y-3 scale-[0.97] opacity-0"
          } ${
            darkTheme
              ? "bg-[#161a20]/95 border-[#2a2f38] backdrop-blur-xl"
              : "bg-White/95 border-Border backdrop-blur-xl"
          }`}
        >
          <div className="relative flex items-center gap-3 bg-[linear-gradient(135deg,#0A4DDC_0%,#4338ca_100%)] px-4 py-3.5 text-White">
            <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-White/15">
              <span className="absolute inset-0 rounded-2xl bg-White/20 blur-md" />
              <HiSparkles size={20} className="relative" />
            </div>
            <div className="flex-1">
              <div className="font-semibold leading-tight">Ask the Docs</div>
              <div className="text-xs text-White/75">
                {productLabel} &bull; AI-powered
              </div>
            </div>
            <button
              onClick={handleClose}
              aria-label="Close chat"
              className="rounded-full p-1.5 transition hover:bg-White/15"
            >
              <HiXMark size={20} />
            </button>
          </div>

          <div
            ref={listRef}
            className={`flex-1 space-y-3 overflow-y-auto p-4 text-sm [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full ${
              darkTheme
                ? "bg-[#12151a] text-White [&::-webkit-scrollbar-thumb]:bg-White/10"
                : "bg-[linear-gradient(180deg,#F6F8FC_0%,#ffffff_140px)] text-Text [&::-webkit-scrollbar-thumb]:bg-Border"
            }`}
          >
            {isEmptyState ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <div className="relative flex h-14 w-14 items-center justify-center rounded-3xl bg-[linear-gradient(135deg,#0A4DDC_0%,#5b3df0_100%)] text-White shadow-lg shadow-cfBlue/30">
                  <span className="absolute inset-0 rounded-3xl bg-[#5b3df0] opacity-30 blur-lg animate-pulse" />
                  <HiSparkles size={26} className="relative" />
                </div>
                <div
                  className={`text-base font-semibold ${
                    darkTheme ? "text-White" : "text-Text"
                  }`}
                >
                  Ask the Docs
                </div>
                <p
                  className={`max-w-[260px] text-sm leading-relaxed ${
                    darkTheme ? "text-White/60" : "text-DarkGray"
                  }`}
                >
                  Ask me anything about {productLabel} -- I'll answer
                  straight from the docs.
                </p>
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex items-end gap-2 ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {m.role === "assistant" && (
                    <div className="mb-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#0A4DDC_0%,#5b3df0_100%)] text-White">
                      <HiSparkles size={12} />
                    </div>
                  )}
                  <div
                    className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 leading-relaxed shadow-sm ${
                      m.role === "user"
                        ? "whitespace-pre-wrap rounded-br-md bg-[linear-gradient(135deg,#0A4DDC_0%,#2f5fe0_100%)] text-White"
                        : darkTheme
                        ? "rounded-bl-md bg-[#1e242d] text-White"
                        : "rounded-bl-md bg-White text-Text"
                    }`}
                  >
                    {m.role === "assistant" ? (
                      <div
                        className={`prose prose-sm max-w-none [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 first:[&>*]:mt-0 last:[&>*]:mb-0 ${
                          darkTheme ? "prose-invert" : ""
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: formatAssistantText(m.text),
                        }}
                      />
                    ) : (
                      m.text
                    )}
                    {m.sources?.length > 0 && (
                      <div
                        className={`mt-2.5 flex flex-wrap gap-1.5 border-t pt-2.5 text-xs ${
                          darkTheme ? "border-White/10" : "border-Border"
                        }`}
                      >
                        {m.sources.map((s, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() =>
                              m.version?.slug &&
                              s.slug &&
                              // A client-side route push doesn't update the
                              // article body -- Home.js only loads content
                              // through its own sidebar-click state
                              // (selectedHighlight/bodyData) or a fresh page
                              // load resolving the URL on mount, so this has
                              // to be a real navigation, not react-router's.
                              window.location.assign(
                                `/cloudframe/${routePrefix}/${m.version.slug}/${s.slug}`
                              )
                            }
                            disabled={!m.version?.slug || !s.slug}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition ${
                              darkTheme
                                ? "bg-White/10 text-White/70 enabled:hover:bg-White/20 enabled:hover:text-White"
                                : "bg-WhiteBg text-DarkGray enabled:hover:bg-cfBlue/10 enabled:hover:text-cfBlue"
                            } disabled:cursor-default`}
                          >
                            <HiOutlineDocumentText size={12} />
                            {s.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex items-end gap-2">
                <div className="mb-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#0A4DDC_0%,#5b3df0_100%)] text-White">
                  <HiSparkles size={12} />
                </div>
                <div
                  className={`flex items-center gap-1.5 rounded-2xl rounded-bl-md px-3.5 py-3 shadow-sm ${
                    darkTheme ? "bg-[#1e242d]" : "bg-White"
                  }`}
                >
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cfBlue [animation-delay:-0.2s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cfBlue" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cfBlue [animation-delay:0.2s]" />
                </div>
              </div>
            )}
          </div>

          <div
            className={`flex items-center gap-2 p-3 ${
              darkTheme ? "bg-[#161a20]" : "bg-White"
            }`}
          >
            <div
              className={`flex flex-1 items-center rounded-full border px-4 py-1 transition focus-within:border-cfBlue ${
                darkTheme
                  ? "border-[#2a2f38] bg-[#0f1217]"
                  : "border-Border bg-WhiteBg"
              }`}
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask a question..."
                disabled={loading}
                className={`flex-1 bg-transparent py-2 text-sm outline-none ${
                  darkTheme ? "text-White placeholder:text-White/40" : "text-Text"
                }`}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              aria-label="Send"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#0A4DDC_0%,#5b3df0_100%)] text-White shadow-md shadow-cfBlue/25 transition disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
            >
              <IoSend size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
